import { ClearOutlined, CloseOutlined, DownOutlined, LoadingOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Dropdown, Flex, InputNumber, message, Segmented, Space, Spin, Tooltip } from "antd";
import { MenuProps } from "antd/lib";
import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useState, useRef, Dispatch, SetStateAction } from "react";
import { StyledButton } from "src/styled_components/StyledButton";
import { StyledPageHeader } from "src/styled_components/StyledPageHeader";
import "./Shared/SEMAnalysis.scss";
import ResidualAnalysis from "./ResidualAnalysis/ResidualAnalysis";
import ProtrusionAnalysis from "./ProtrusionAnalysis/ProtrusionAnalysis";
import GeneralAnalysis from "./GeneralAnalysis/GeneralAnalysis";
import FlowabilityAnalysis from "./FlowabilityAnalysis/FlowabilityAnalysis";
import PaintFilmAnalysis from "./PaintFilmAnalysis/PaintFilmAnalysis";
import { useHistory, useParams } from "react-router-dom";
import NewPaintFilmAnalysisModal from "./Shared/NewAnalysisModals/NewPaintFilmAnalysisModal";
import NewContrastiveAnalysisModal from "./Shared/NewAnalysisModals/NewContrastiveAnalysisModal";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "src/store/configureStore";
import { AsyncStates } from "src/constants";
import {
  analysisTypesRequest,
  fetchSemAnalysisListClear,
  fetchSemAnalysisMaskDataClear,
} from "src/store/actions/semAnalysis";
import NewSingleImageAnalysisModal from "./Shared/NewAnalysisModals/NewSingleImageAnalysisModal";
import SeedAnalysis from "./SeedAnalysis/SeedAnalysis";
import NewFiberAnalysisModal from "./Shared/NewAnalysisModals/NewFiberAnalysisModal";
import FiberAnalysis from "./FiberAnalysis/FiberAnalysis";
import NewWhiteObjectAnalysisModal from "./Shared/NewAnalysisModals/NewWhiteObjectAnalysisModal";
import WhiteObjectAnalysis from "./WhiteObjectAnalysis/WhiteObjectAnalysis";
import useTranslate, { TranslateFunc } from "src/utils/useTranslate";
import Konva from 'konva';
import { Drawer, } from 'antd';
import useImage from "use-image";
import NewBlackObjectAnalysisModal from "./Shared/NewAnalysisModals/NewBlackObjectAnalysisModal";
import BlackObjectAnalysis from "./BlackObjectAnalysis/BlackObjectAnalysis";

export enum IMAGE_ANALYSIS_TYPES {
  RESIDUAL_ANALYSIS = "residual-analysis",
  FLOWABILITY_ANALYSIS = "flowability-analysis",
  PROTRUSION_ANALYSIS = "protrusion-analysis",
  GENERAL_ANALYSIS = "general-analysis",
  PAINT_FILM_ANALYSIS = "paint-film-analysis",
  SEED_ANALYSIS = "seed-analysis",
  FIBER_ANALYSIS = "fiber-analysis",
  WHITE_OBJECT_ANALYSIS = "white-object-analysis",
  BLACK_OBJECT_ANALYSIS = "black-object-analysis"
}

export const imageAnalysisTypesTranslationMapper = (
  type: string,
  t: TranslateFunc
) => {
  type = type.toLowerCase();
  switch (type) {
    case "residual analysis":
      return t("sem.residualAnalysis");
    case "protrusion analysis":
      return t("sem.protrusionAnalysis");
    case "flowability analysis":
      return t("sem.flowabilityAnalysis");
    case "paint film analysis":
      return t("sem.paintFilmAnalysis");
    case "fiber analysis":
      return t("sem.fiberAnalysis");
    case "seed analysis":
      return t("sem.seedAnalysis");
    case "general analysis":
      return t("sem.generalAnalysis");
    case "white object analysis":
      return t("sem.whiteObjectAnalysis");
    case "black object analysis":
      return "Black Object Analysis"
  }
};

const SEMAnalysisWrapper = () => {
  const [t] = useTranslate();
  const params = useParams<any>();
  const history = useHistory();
  const dispatch = useDispatch();
  const [showPaintFilmAnalysisModal, setShowPaintFilmAnalysisModal] =
    useState(false);
  const [showFlowabilityAnalysisModal, setShowFlowabilityAnalysisModal] =
    useState<boolean>(false);
  const [showProtrusionAnalysisModal, setShowProtrusionAnalysisModal] =
    useState<boolean>(false);
  const [showContrastiveAnalysisModal, setShowContrastiveAnalysisModal] =
    useState<boolean>(false);
  const [showGeneralAnalysisModal, setShowGeneralAnalysisModal] =
    useState<boolean>(false);
  const [showSeedAnalysisModal, setShowSeedAnalysisModal] =
    useState<boolean>(false);
  const [showFiberAnalysisModal, setShowFiberAnalysisModal] =
    useState<boolean>(false);
  const [showWhiteObjectAnalysisModal, setShowWhiteObjectAnalysisModal] =
    useState<boolean>(false);
  const [showBlackObjectAnalysisModal, setShowBlackObjectAnalysisModal] = useState<boolean>(false);

  const [currentTab, setCurrentTab] = useState<string>();
  const { analysisTypes, analysisTypesStatus } = useSelector(
    (state: StoreState) => state.semAnalysis
  );

  useLayoutEffect(() => {
    if (analysisTypesStatus === AsyncStates.INITIAL)
      dispatch(analysisTypesRequest());
  }, [analysisTypesStatus, dispatch]);

  useEffect(() => {
    if (!params.analysisType && Object.keys(analysisTypes).length > 0) {
      history.push(`/sem-analysis/${Object.keys(analysisTypes)[0]}`);
    } else {
      setCurrentTab(params.analysisType);
    }
  }, [history, params, analysisTypes]);

  const dropDownOptions: MenuProps["items"] = useMemo(() => {
    return Object.keys(analysisTypes || {}).map((key) => {
      return {
        key: key,
        label: (
          <>
            {imageAnalysisTypesTranslationMapper(
              analysisTypes[key as keyof typeof analysisTypes],
              t
            )}
          </>
        ),
        onClick: (e: any) => {
          e.domEvent.stopPropagation();
          if (key === IMAGE_ANALYSIS_TYPES.RESIDUAL_ANALYSIS)
            setShowContrastiveAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.FLOWABILITY_ANALYSIS)
            setShowFlowabilityAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.PROTRUSION_ANALYSIS)
            setShowProtrusionAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS)
            setShowGeneralAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.PAINT_FILM_ANALYSIS)
            setShowPaintFilmAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.SEED_ANALYSIS)
            setShowSeedAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.FIBER_ANALYSIS)
            setShowFiberAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.WHITE_OBJECT_ANALYSIS)
            setShowWhiteObjectAnalysisModal(true);
          if (key === IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS)
            setShowBlackObjectAnalysisModal(true);
        },
      };
    });
  }, [analysisTypes, t]);

  if (analysisTypesStatus !== AsyncStates.SUCCESS)
    return <Spin indicator={<LoadingOutlined />} spinning={true} />;

  return (
    <Fragment>
      <Space
        direction="vertical"
        style={{ width: "100%", marginBottom: 20 }}
        size="large"
      >
        <StyledPageHeader
          ghost={false}
          title={t("sem.imageAnalysis")}
          extra={
            <Space size="large">
              <Dropdown
                menu={{
                  items: dropDownOptions,
                }}
                placement="bottomRight"
                trigger={["click"]}
              >
                <StyledButton type="primary" size="middle">
                  {t("sem.newAnalysis")}
                  <DownOutlined />
                </StyledButton>
              </Dropdown>
            </Space>
          }
        />
      </Space>

      <Flex gap={12}>
        <Segmented
          value={currentTab}
          options={Object.keys(analysisTypes)
            .filter((t) => t !== IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS)
            .map((key) => ({
              label: imageAnalysisTypesTranslationMapper(
                analysisTypes[key as keyof typeof analysisTypes],
                t
              ),
              value: key,
            }))}
          onChange={(value) => {
            dispatch(fetchSemAnalysisListClear());
            dispatch(fetchSemAnalysisMaskDataClear());
            history.push(`/sem-analysis/${value}`);
          }}
        />

        {Object.keys(analysisTypes).find(
          (t) => t === IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS
        ) && (
            <Segmented
              value={currentTab}
              options={[
                {
                  label: imageAnalysisTypesTranslationMapper(
                    analysisTypes[
                    IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS as keyof typeof analysisTypes
                    ],
                    t
                  ),
                  value: IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS,
                },
              ]}
              onChange={(value) => {
                dispatch(fetchSemAnalysisListClear());
                dispatch(fetchSemAnalysisMaskDataClear());
                history.push(
                  `/sem-analysis/${IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS}`
                );
              }}
            />
          )}
      </Flex>

      {currentTab === IMAGE_ANALYSIS_TYPES.RESIDUAL_ANALYSIS && (
        <ResidualAnalysis />
      )}
      {currentTab === IMAGE_ANALYSIS_TYPES.FLOWABILITY_ANALYSIS && (
        <FlowabilityAnalysis />
      )}
      {currentTab === IMAGE_ANALYSIS_TYPES.PROTRUSION_ANALYSIS && (
        <ProtrusionAnalysis />
      )}
      {currentTab === IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS && (
        <GeneralAnalysis />
      )}
      {currentTab === IMAGE_ANALYSIS_TYPES.PAINT_FILM_ANALYSIS && (
        <PaintFilmAnalysis />
      )}
      {currentTab === IMAGE_ANALYSIS_TYPES.SEED_ANALYSIS && <SeedAnalysis />}
      {currentTab === IMAGE_ANALYSIS_TYPES.FIBER_ANALYSIS && <FiberAnalysis />}
      {currentTab === IMAGE_ANALYSIS_TYPES.WHITE_OBJECT_ANALYSIS && (
        <WhiteObjectAnalysis />
      )}

      {currentTab === IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS && <BlackObjectAnalysis />}


      {showPaintFilmAnalysisModal && (
        <NewPaintFilmAnalysisModal
          showPaintFilmAnalysisModal={showPaintFilmAnalysisModal}
          setShowPaintFilmAnalysisModal={setShowPaintFilmAnalysisModal}
        />
      )}
      {showContrastiveAnalysisModal && (
        <NewContrastiveAnalysisModal
          newContrastiveAnalysisModal={showContrastiveAnalysisModal}
          setNewContrastiveAnalysisModal={setShowContrastiveAnalysisModal}
        />
      )}
      {showFlowabilityAnalysisModal && (
        <NewSingleImageAnalysisModal
          showSingleImageAnalysisModal={showFlowabilityAnalysisModal}
          setShowSingleImageAnalysisModal={setShowFlowabilityAnalysisModal}
          analysisType={
            analysisTypes[
            IMAGE_ANALYSIS_TYPES.FLOWABILITY_ANALYSIS as keyof typeof analysisTypes
            ]
          }
        />
      )}
      {showProtrusionAnalysisModal && (
        <NewSingleImageAnalysisModal
          showSingleImageAnalysisModal={showProtrusionAnalysisModal}
          setShowSingleImageAnalysisModal={setShowProtrusionAnalysisModal}
          analysisType={
            analysisTypes[
            IMAGE_ANALYSIS_TYPES.PROTRUSION_ANALYSIS as keyof typeof analysisTypes
            ]
          }
        />
      )}
      {showGeneralAnalysisModal && (
        <NewSingleImageAnalysisModal
          showSingleImageAnalysisModal={showGeneralAnalysisModal}
          setShowSingleImageAnalysisModal={setShowGeneralAnalysisModal}
          analysisType={
            analysisTypes[
            IMAGE_ANALYSIS_TYPES.GENERAL_ANALYSIS as keyof typeof analysisTypes
            ]
          }
        />
      )}
      {showSeedAnalysisModal && (
        <NewSingleImageAnalysisModal
          showSingleImageAnalysisModal={showSeedAnalysisModal}
          setShowSingleImageAnalysisModal={setShowSeedAnalysisModal}
          analysisType={
            analysisTypes[
            IMAGE_ANALYSIS_TYPES.SEED_ANALYSIS as keyof typeof analysisTypes
            ]
          }
        />
      )}
      {showFiberAnalysisModal && (
        <NewFiberAnalysisModal
          showFiberAnalysisModal={showFiberAnalysisModal}
          setShowFiberAnalysisModal={setShowFiberAnalysisModal}
        />
      )}
      {showWhiteObjectAnalysisModal && (
        <NewWhiteObjectAnalysisModal
          showWhiteObjectAnalysisModal={showWhiteObjectAnalysisModal}
          setShowWhiteObjectAnalysisModal={setShowWhiteObjectAnalysisModal}
        />
      )}

      {showBlackObjectAnalysisModal && (
        <NewBlackObjectAnalysisModal
          showBlackObjectAnalysisModal={showBlackObjectAnalysisModal}
          setShowBlackObjectAnalysisModal={setShowBlackObjectAnalysisModal}
        />
      )}
    </Fragment>
  );
};

export default SEMAnalysisWrapper;


// const CONTAINER_WIDTH = 1000;
// const CONTAINER_HEIGHT = 1000;

export interface RectangleScale {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

const maxRectangleCanbeAdded = 1
const textPadding = 10


type OnSavePayload = {
  scaleValues: RectangleScale[]
  selectedRect: RectangleScale | null
  imageUrl: string
}

type ImageScaleAnnotatorTypesProps = {
  isOpen: boolean
  onClose: () => void
  imageUrl: string
  onSave: (payload: OnSavePayload) => void
  scaleValues: RectangleScale[]
  setScaleValues: Dispatch<SetStateAction<RectangleScale[]>>
  mode: "create" | "edit"
}

export const ImageScaleAnnotator = ({ isOpen = false, onClose, imageUrl, onSave, scaleValues = [], setScaleValues, mode }: ImageScaleAnnotatorTypesProps) => {
  const [t] = useTranslate();
  const [image, status] = useImage(imageUrl);
  const [selectedRect, setSelectedRect] = useState<RectangleScale | null>(null);

  const [imageDimensions, setImageDimensions] = useState({
    width: image?.width,
    height: image?.height
  })

  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage | null>(null);
  const layerRef = useRef<Konva.Layer | null>(null);
  const transformerRef = useRef<Konva.Transformer | null>(null);
  const imageRef = useRef<Konva.Image | null>(null);
  const isDrawing = useRef(false);
  const newRectangle = useRef<Konva.Rect | null>(null);
  const selectedRectId = useRef<string | null>(null);
  const rectangleCount = useRef<number>(scaleValues.length);

  useEffect(() => {
    rectangleCount.current = scaleValues.length
  }, [scaleValues.length])


  const updateRectangleDimensions = (width: number, height: number) => {
    if (!layerRef.current || !selectedRectId.current) return;

    const rect = layerRef.current.findOne(`#${selectedRectId.current}`) as Konva.Rect;
    if (!rect) return;

    rect.width(width);
    rect.height(height);

    const text = layerRef.current.find('Text').find(t => {
      const textY = t.y();
      const rectY = rect.y();
      return Math.abs(textY - (rectY - 20)) < 1;
    })

    if (text) {
      (text as Konva.Text).text(`${Math.round(width)} x ${Math.round(height)}`);
    }

    setScaleValues(prev =>
      prev.map(r =>
        r.id === selectedRectId.current
          ? { ...r, width, height }
          : r
      )
    );

    layerRef.current.batchDraw();
  };

  const addImageToStage = useCallback((img: HTMLImageElement) => {
    if (!layerRef.current || !stageRef.current) return;

    setImageDimensions({ width: img.width, height: img.height })

    try {
      stageRef.current.width(img.width)
      stageRef.current.height(img.height)

      if (imageRef.current) {
        imageRef.current.destroy();
        imageRef.current = null;
      }

      const konvaImage = new Konva.Image({
        image: img,
        width: img.width,
        height: img.height,
        listening: false,
      });

      imageRef.current = konvaImage;
      layerRef.current.add(konvaImage);
      konvaImage.moveToBottom();
      stageRef.current.batchDraw()
    } catch (error) {
      console.error('Error adding image to stage:', error);
    }
  }, [])

  useEffect(() => {
    if (!containerRef.current) return;

    if (stageRef.current) {
      stageRef.current.destroy();
      stageRef.current = null;
      layerRef.current = null;
      transformerRef.current = null;
      imageRef.current = null;
    }

    const stage = new Konva.Stage({
      container: containerRef.current,
      width: imageDimensions.width,
      height: imageDimensions.height,
    });
    stageRef.current = stage;

    const layer = new Konva.Layer();
    layerRef.current = layer;
    stage.add(layer);

    const transformer = new Konva.Transformer({
      rotateEnabled: false,
      boundBoxFunc: (oldBox, newBox) => {
        return newBox.width < 5 || newBox.height < 5 ? oldBox : newBox;
      },
    });
    transformerRef.current = transformer;
    layer.add(transformer);

    if (image && status === 'loaded') {
      addImageToStage(image);
      if (!!scaleValues.length) {
        scaleValues.forEach((scaleValue, index) => {

          const width = Math.abs(scaleValue.width);
          const height = Math.abs(scaleValue.height);

          const startX = scaleValue.width < 0 ? scaleValue.x + scaleValue.width : scaleValue.x;
          const startY = scaleValue.height < 0 ? scaleValue.y + scaleValue.height : scaleValue.y;

          const rect = new Konva.Rect({
            ...scaleValue,
            stroke: '#00ff00',
            strokeWidth: 2,
            dash: [5, 5],
            draggable: true
          });


          const rectHeight = rect.height();

          const text = new Konva.Text({
            x: startX,
            y: startY < 25 ? (startY + rectHeight + textPadding) : startY - 20,
            text: `${Math.round(width)} x ${Math.round(height)}`,
            fontSize: 12,
            fill: '#00ff00',
          });

          newRectangle.current = rect;

          rect.on('dragstart', () => {
            text.hide();
            layerRef.current?.batchDraw();
          });

          rect.on('click tap', () => {
            selectedRectId.current = rect.id();
            transformer.nodes([rect]);
            setSelectedRect({
              id: rect.id(),
              width: rect.width(),
              height: rect.height(),
              x: rect.x(),
              y: rect.y()
            });
          });

          rect.on('dragend', () => {
            if (!stageRef.current || !layerRef.current) return;

            const stage = stageRef.current;
            const layer = layerRef.current;

            const rectWidth = rect.width();
            const rectHeight = rect.height();

            const newX = rect.x();
            const newY = rect.y();

            const isOutOfBounds = newX < 0 || newY < 0 || newX + rectWidth > stage.width() || newY + rectHeight > stage.height();

            if (isOutOfBounds) {
              rect.position({ x: scaleValue.x, y: scaleValue.y });
              text.position({ x: scaleValue.x, y: scaleValue.y - 20 });
            } else {
              text.position({ x: newX, y: newY < 25 ? (newY + rectHeight + textPadding) : newY - 20 });
              setScaleValues((prev) => {
                const newState = JSON.parse(JSON.stringify(prev))
                newState[index]["x"] = newX
                newState[index]["y"] = newY
                return newState
              })
            }

            text.show();
            layer.batchDraw();
          });

          rect.on('transform', () => {
            const node = rect;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();

            node.scaleX(1);
            node.scaleY(1);

            const newWidth = Math.max(5, node.width() * scaleX);
            const newHeight = Math.max(5, node.height() * scaleY);

            node.width(newWidth);
            node.height(newHeight);

            text.text(`${Math.round(newWidth)} x ${Math.round(newHeight)}`);
            text.x(node.x());
            text.y(node.y() - 20);

            setSelectedRect({
              id: node.id(),
              width: newWidth,
              height: newHeight,
              x: node.x(),
              y: node.y()
            });

            setScaleValues((prev) => {
              const newState = JSON.parse(JSON.stringify(prev))
              newState[index]["width"] = newWidth
              newState[index]["height"] = newHeight
              return newState
            })

          });

          layerRef.current?.add(rect);
          layerRef.current?.add(text);
          transformer.nodes([rect]);
        })
        layerRef.current.batchDraw();
      }
    }

    stage.on('mousedown touchstart', (e) => {
      if (e.target === stage && imageRef.current) {
        selectedRectId.current = null;
        setSelectedRect(null);
        transformer.nodes([]);

        const pos = stage.getPointerPosition();
        if (!pos) return;

        isDrawing.current = true;

        const rect = new Konva.Rect({
          x: pos.x,
          y: pos.y,
          width: 0,
          height: 0,
          stroke: '#00ff00',
          strokeWidth: 2,
          dash: [5, 5],
          id: Date.now().toString(),
        });
        newRectangle.current = rect;
        layer.add(rect);
      }
    });

    stage.on('mousemove touchmove', () => {
      if (!isDrawing.current || !newRectangle.current) return;

      if (rectangleCount.current >= maxRectangleCanbeAdded) {
        message.info(`Max ${maxRectangleCanbeAdded} can be added`)
        layer.batchDraw();
        isDrawing.current = false
        return
      }

      const pos = stage.getPointerPosition();
      if (!pos) return;

      const rect = newRectangle.current;
      rect.width(pos.x - rect.x());
      rect.height(pos.y - rect.y());
      layer.batchDraw();
    });

    stage.on('mouseup touchend', () => {
      if (!isDrawing.current || !newRectangle.current || !imageRef.current) return;

      isDrawing.current = false;
      const rect = newRectangle.current;
      const width = Math.abs(rect.width());
      const height = Math.abs(rect.height());

      const startX = rect.width() < 0 ? rect.x() + rect.width() : rect.x();
      const startY = rect.height() < 0 ? rect.y() + rect.height() : rect.y();
      rect.destroy();

      if (width > 5 && height > 5) {
        const finalRect = new Konva.Rect({
          x: startX,
          y: startY,
          width,
          height,
          stroke: '#00ff00',
          strokeWidth: 2,
          draggable: true,
          id: Date.now().toString(),
        });

        const text = new Konva.Text({
          x: startX,
          y: startY - 20,
          text: `${Math.round(width)} x ${Math.round(height)}`,
          fontSize: 12,
          fill: '#00ff00',
        });


        finalRect.on('dragstart', () => {
          text.hide();
          layerRef.current?.batchDraw();
        });

        finalRect.on('click tap', () => {
          selectedRectId.current = finalRect.id();
          transformer.nodes([finalRect]);
          setSelectedRect({
            id: finalRect.id(),
            width: finalRect.width(),
            height: finalRect.height(),
            x: finalRect.x(),
            y: finalRect.y()
          });
        });

        finalRect.on('dragend', () => {
          if (!stageRef.current || !layerRef.current) return;

          const stage = stageRef.current;
          const layer = layerRef.current;

          const rectWidth = finalRect.width();
          const rectHeight = finalRect.height();

          const newX = finalRect.x();
          const newY = finalRect.y();

          setScaleValues((prev) => {
            return prev.map((r) => {
              if (r.id !== finalRect.id()) return r;

              const isOutOfBounds = newX < 0 || newY < 0 || newX + rectWidth > stage.width() || newY + rectHeight > stage.height();

              if (isOutOfBounds) {
                finalRect.position({ x: r.x, y: r.y });
                text.position({ x: r.x, y: r.y - 20 });
              } else {
                const textPadding = 10
                text.position({ x: newX, y: newY < 25 ? (newY + rectHeight + textPadding) : newY - 20 });
                return { ...r, x: newX, y: newY };
              }
              return r;
            });
          });
          text.show();
          layer.batchDraw();
        });

        finalRect.on('transform', () => {
          const node = finalRect;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();

          node.scaleX(1);
          node.scaleY(1);

          const newWidth = Math.max(5, node.width() * scaleX);
          const newHeight = Math.max(5, node.height() * scaleY);

          node.width(newWidth);
          node.height(newHeight);

          text.text(`${Math.round(newWidth)} x ${Math.round(newHeight)}`);
          text.x(node.x());
          text.y(node.y() - 20);

          setSelectedRect({
            id: node.id(),
            width: newWidth,
            height: newHeight,
            x: node.x(),
            y: node.y()
          });

          setScaleValues(prev =>
            prev.map(r =>
              r.id === node.id()
                ? {
                  ...r,
                  width: newWidth,
                  height: newHeight,
                  x: node.x(),
                  y: node.y()
                }
                : r
            )
          );

        });

        layer.add(finalRect);
        layer.add(text);

        selectedRectId.current = finalRect.id();
        transformer.nodes([finalRect]);

        setScaleValues((prev) => [
          ...prev,
          {
            id: finalRect.id(),
            x: startX,
            y: startY,
            width,
            height,
          },
        ]);
      }

      newRectangle.current = null;
      layer.batchDraw();
    });

    return () => {
      stage.destroy();
      stageRef.current = null;
      layerRef.current = null;
      transformerRef.current = null;
      imageRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, image, status, addImageToStage, setScaleValues, imageDimensions.width, imageDimensions.height]);


  const handleUndo = () => {
    if (!layerRef.current) return;

    const lastRect = scaleValues[scaleValues.length - 1];
    if (lastRect) {
      const nodes = layerRef.current.find(`#${lastRect.id}`);
      nodes.forEach(node => node.destroy());

      const texts = layerRef.current.find('Text');
      if (texts.length > 0) {
        texts[texts.length - 1].destroy();
      }

      setScaleValues(prev => prev.slice(0, -1));
      selectedRectId.current = null;
      setSelectedRect(null);
      if (transformerRef.current) {
        transformerRef.current.nodes([]);
      }
      layerRef.current.batchDraw();
    }
  };

  // const handleClear = () => {
  //   if (!layerRef.current || !transformerRef.current) return;

  //   const layer = layerRef.current;
  //   const transformer = transformerRef.current;

  //   const texts = layerRef.current.find('Text');

  //   scaleValues.forEach((rectangle, index) => {
  //     const nodes = layer.find(`#${rectangle.id}`);
  //     nodes.forEach(node => node.destroy());
  //     if (texts[index]) {
  //       texts[index].destroy();
  //     }
  //   })

  //   newRectangle.current?.destroy();
  //   newRectangle.current = null;

  //   transformer.nodes([]);
  //   selectedRectId.current = null;
  //   setSelectedRect(null);
  //   setScaleValues([]);
  //   layer.batchDraw();
  // };


  const handleSave = () => {
    if (mode === "edit" && !scaleValues.length) {
      message.error("Please add a scale to save");
      return
    }
    onSave({ scaleValues, selectedRect, imageUrl })
  }

  return (
    <Drawer
      title="Image Annotator"
      placement="bottom"
      height={"100%"}
      onClose={() => {
        onClose()
        setScaleValues([])
        selectedRectId.current = null
        setSelectedRect(null)
      }}
      open={isOpen}
      extra={
        <Space>
          <Button
            // icon={<UndoOutlined />}
            icon={<ClearOutlined />}
            onClick={handleUndo}
            disabled={scaleValues.length === 0}
          >
            {t("common.clear")}
          </Button>
          {/* <Button
            icon={<ClearOutlined />}
            onClick={handleClear}
            disabled={scaleValues.length === 0}
            danger
          >
            {t("common.clearAll")}
          </Button> */}
          <Button
            icon={<SaveOutlined />}
            type="primary"
            onClick={handleSave}
            disabled={scaleValues.length === 0}
          >
            {t("common.save")}
          </Button>
        </Space>


      }
    >
      <div style={{
        position: 'relative',
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}>
        <div
          ref={containerRef}
          style={{
            position: 'relative',
            border: '1px solid #ccc',
          }}
        />

        {selectedRect && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              backgroundColor: 'white',
              padding: 12,
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Tooltip title="Width">
                  <InputNumber
                    size="small"
                    min={1}
                    max={imageDimensions.width}
                    value={Math.round(selectedRect.width)}
                    onChange={(value) => {
                      if (value) {
                        updateRectangleDimensions(value, selectedRect.height);
                      }
                    }}
                  />
                </Tooltip>
                <span>×</span>
                <Tooltip title="Height">
                  <InputNumber
                    size="small"
                    min={1}
                    max={imageDimensions.height}
                    value={Math.round(selectedRect.height)}
                    onChange={(value) => {
                      if (value) {
                        updateRectangleDimensions(selectedRect.width, value);
                      }
                    }}
                  />
                </Tooltip>
              </div>
              <Tooltip title="Close">
                <Button
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={() => {
                    selectedRectId.current = null;
                    setSelectedRect(null);
                    if (transformerRef.current) {
                      transformerRef.current.nodes([]);
                      layerRef.current?.batchDraw();
                    }
                  }}
                />
              </Tooltip>
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
};